import React, { useState } from "react";
import { Plus } from "iconoir-react";
import { useNotificationToast } from "@/components/ui/NotificationToast";

interface ProductData {
  name: string;
  price: string;
  location: string;
  image?: string;
  imageUrl?: string;
}

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddProduct: (productData: ProductData) => void;
}

const AddProductModal: React.FC<AddProductModalProps> = ({
  isOpen,
  onClose,
  onAddProduct,
}) => {
  const [productData, setProductData] = useState({
    name: "",
    price: "",
    location: "",
  });

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [, setImageFile] = useState<File | null>(null);
  const { showToast } = useNotificationToast();

  const handleInputChange = (field: string, value: string) => {
    setProductData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const imageUrl = URL.createObjectURL(file);
      setImageFile(file);
      setImagePreview(imageUrl);
    }
  };

  const handleSubmit = () => {
    if (
      !productData.name.trim() ||
      !productData.price.trim() ||
      !productData.location.trim()
    ) {
      return;
    }

    const newProduct: ProductData = {
      name: productData.name,
      price: productData.price,
      location: productData.location,
      imageUrl: imagePreview || undefined,
    };

    onAddProduct(newProduct);
    onClose();

    // Show success toast
    showToast("Tạo mới sản phẩm thành công", "success");

    // Reset form
    setProductData({
      name: "",
      price: "",
      location: "",
    });
    setImagePreview(null);
    setImageFile(null);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop with glassmorphism */}
      <div className="fixed inset-0 z-50 backdrop-blur-strong bg-midnight-blue/20" />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-60">
        <div className="w-80 px-4 py-6 glass-primary inline-flex flex-col justify-center items-start gap-6 relative">
          <div className="self-stretch flex flex-col justify-start items-start gap-4">
            <div className="self-stretch text-center justify-start text-midnight-blue text-xl font-semibold font-inter leading-normal">
              Thêm sản phẩm
            </div>

            <div className="self-stretch inline-flex justify-start items-center gap-2.5 overflow-hidden">
              <div className="w-80 p-3.5 rounded-md inline-flex flex-col justify-center items-start gap-6">
                {/* Upload Image */}
                <div className="self-stretch flex flex-col justify-start items-start gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="product-image-upload"
                  />

                  {imagePreview ? (
                    <div className="relative w-full">
                      <img
                        src={imagePreview}
                        alt="Product preview"
                        className="w-full h-32 object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setImageFile(null);
                        }}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="product-image-upload"
                      className="self-stretch px-4 py-3.5 rounded-md outline outline-1 outline-offset-[-1px] outline-[#D1D1D0] inline-flex justify-between items-center cursor-pointer hover:bg-gray-50"
                    >
                      <div className="size- flex justify-start items-center gap-2.5">
                        <div className="size-5 relative overflow-hidden">
                          <Plus className="w-5 h-5 text-[#1E3A3A]" />
                        </div>
                        <div className="text-center justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-tight">
                          Tải hình ảnh lên
                        </div>
                      </div>
                    </label>
                  )}
                </div>

                {/* Product Name */}
                <div className="self-stretch flex flex-col justify-start items-start gap-2">
                  <div className="size- inline-flex justify-start items-end gap-1">
                    <div className="text-center justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-tight">
                      Tên sản phẩm
                    </div>
                    <div className="text-center justify-start text-[#E06448] text-[10px] font-normal font-sora leading-none">
                      *Bắt buộc
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-between items-center">
                    <input
                      type="text"
                      value={productData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      placeholder="Nhập..."
                      className="flex-1 p-3.5 rounded-md outline outline-1 outline-offset-[-1px] outline-[#D1D1D0] text-[#6F6F6B] text-xs font-normal font-sora leading-none bg-white"
                    />
                  </div>
                </div>

                {/* Price */}
                <div className="self-stretch flex flex-col justify-start items-start gap-2">
                  <div className="size- inline-flex justify-start items-end gap-1">
                    <div className="text-center justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-tight">
                      Giá
                    </div>
                    <div className="text-center justify-start text-[#E06448] text-[10px] font-normal font-sora leading-none">
                      *Bắt buộc
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-between items-center">
                    <input
                      type="text"
                      value={productData.price}
                      onChange={(e) =>
                        handleInputChange("price", e.target.value)
                      }
                      placeholder="Nhập..."
                      className="flex-1 p-3.5 rounded-md outline outline-1 outline-offset-[-1px] outline-[#D1D1D0] text-[#6F6F6B] text-xs font-normal font-sora leading-none bg-white"
                    />
                  </div>
                </div>

                {/* Location */}
                <div className="self-stretch flex flex-col justify-start items-start gap-2">
                  <div className="size- inline-flex justify-start items-end gap-1">
                    <div className="text-center justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-tight">
                      Vị trí nhận đồ
                    </div>
                    <div className="text-center justify-start text-[#E06448] text-[10px] font-normal font-sora leading-none">
                      *Bắt buộc
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-between items-center">
                    <input
                      type="text"
                      value={productData.location}
                      onChange={(e) =>
                        handleInputChange("location", e.target.value)
                      }
                      placeholder="Nhập..."
                      className="flex-1 p-3.5 rounded-md outline outline-1 outline-offset-[-1px] outline-[#D1D1D0] text-[#6F6F6B] text-xs font-normal font-sora leading-none bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="self-stretch inline-flex justify-start items-start gap-2">
            <div
              className="flex-1 px-4 py-3.5 bg-neutral-200 rounded-full flex justify-center items-center gap-2.5 cursor-pointer"
              onClick={onClose}
            >
              <div className="text-center justify-start text-[#1E3A3A] text-sm font-medium font-sora leading-none">
                Hủy
              </div>
            </div>
            <div
              className="flex-1 px-4 py-3.5 bg-[#E06448] rounded-full flex justify-center items-center gap-2.5 cursor-pointer"
              onClick={handleSubmit}
            >
              <div className="text-center justify-start text-[#F4E7DA] text-sm font-medium font-sora leading-none">
                Xác nhận
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddProductModal;
